import React from "react";
import { Button, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";
import theme from "../../utils/theme";

const AppButton = ({
  buttonType = "default",
  label,
  disabled = false,
  fullWidth = false,
  onClick,
  size = "medium",
  startIcon,
  endIcon,
  variant = "contained",
  sx,
  isLoading = false,
  ...props
}) => {
  // Define color schemes for different button types using the theme
  const getButtonStyles = () => {
    switch (buttonType) {
      case "primary":
        return {
          backgroundColor:
            variant === "contained"
              ? theme.palette.primary.main
              : "transparent",
          color:
            variant === "contained"
              ? theme.palette.primary.contrastText
              : theme.palette.primary.main,
          "&:hover": {
            backgroundColor:
              variant === "contained"
                ? theme.palette.primary.dark
                : theme.palette.primary.light + "14", // 14 = 8% opacity in hex
          },
        };
      case "secondary":
        return {
          backgroundColor:
            variant === "contained"
              ? theme.palette.secondary.main
              : "transparent",
          color:
            variant === "contained"
              ? theme.palette.secondary.contrastText
              : theme.palette.secondary.main,
          "&:hover": {
            backgroundColor:
              variant === "contained"
                ? theme.palette.secondary.dark
                : theme.palette.secondary.light + "14",
          },
        };
      case "main":
        return {
          backgroundColor:
            variant === "contained" ? theme.palette.info.main : "transparent",
          color:
            variant === "contained"
              ? theme.palette.info.contrastText
              : theme.palette.info.main,
          "&:hover": {
            backgroundColor:
              variant === "contained"
                ? theme.palette.info.dark
                : theme.palette.info.light + "14",
          },
        };
      case "greyed":
        return {
          backgroundColor:
            variant === "contained" ? theme.palette.grey[300] : "transparent",
          color: theme.palette.text.primary,
          "&:hover": {
            backgroundColor:
              variant === "contained"
                ? theme.palette.grey[400]
                : theme.palette.grey[200] + "14",
          },
        };
      case "disabled":
        return {
          backgroundColor:
            variant === "contained" ? theme.palette.grey[300] : "transparent",
          color: theme.palette.text.disabled,
          "&:hover": {
            backgroundColor:
              variant === "contained"
                ? theme.palette.grey[400]
                : theme.palette.grey[200] + "14",
          },
        };
      case "default":
      default:
        return {}; // Use MUI default styling
    }
  };

  return (
    <Button
      variant={variant}
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      onClick={onClick}
      size={size}
      startIcon={!isLoading ? startIcon : null}
      endIcon={!isLoading ? endIcon : null}
      sx={{
        borderRadius: theme.shape.buttonBorderRadius,
        ...getButtonStyles(),
        minWidth: isLoading ? "80px" : "120px",
        ...sx,
      }}
      {...props}
    >
      {isLoading && (
        <CircularProgress
          size={size === "small" ? 20 : size === "large" ? 28 : 24}
          sx={{
            color: getButtonStyles().color || "inherit",
            mr: 1,
          }}
        />
      )}
      {label}
    </Button>
  );
};

AppButton.propTypes = {
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  buttonType: PropTypes.oneOf([
    "primary",
    "secondary",
    "main",
    "greyed",
    "default",
  ]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  label: PropTypes.node.isRequired,
  sx: PropTypes.object,
  isLoading: PropTypes.bool,
};

AppButton.defaultProps = {
  buttonType: "default",
  disabled: false,
  fullWidth: false,
  size: "medium",
  variant: "contained",
  sx: {},
  isLoading: false,
};

export default AppButton;
